import * as React from "react";
import i18n from "../../../i18n/i18n";
import {Popup} from "../../../common/components/shared/popup/popup";
import {HttpError} from "../../../common/components/shared/httpError/httpError";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../common/models/httpError";
import {Api} from "../../../helpers/api";
import ChangePasswordForm from "../../../common/components/shared/changePasswordForm/changePasswordForm";
import {ValidatedValue} from "../../../helpers/validatedValue";
import "./changeUserPasswordPopup.scss";
import Validator from "../../../helpers/validator";
import {errorMessages} from "../../../helpers/errorMessage";
import {Utils} from "../../../helpers/utils";
import {AdministrationService} from "../../services/administrationService";

interface ChangeUserPasswordPopupProps {
    displayPopup: boolean;
    closePopup: () => void;
    username: string;
    userId: string;
    confirmSuccessAction: () => void;
}

interface ChangeUserPasswordPopupState {
    changePasswordResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    newPassword: ValidatedValue<string>;
    confirmPassword: ValidatedValue<string>;
}

export default class ChangeUserPasswordPopup extends React.Component <ChangeUserPasswordPopupProps, ChangeUserPasswordPopupState> {
    constructor(props: ChangeUserPasswordPopupProps) {
        super(props);

        const notEmptyValidator = {
            name: "valueIsNotEmpty",
            fn: Validator.isNotEmpty,
            errorMessage: errorMessages.required
        };

        const passwordValidator = {
            name: "passwordIsCorrect",
            fn: Validator.isCorrectPassword,
            errorMessage: () => `${i18n.t("administration.addUser.errors.incorrectPassword")}`
        };

        this.state = {
            changePasswordResponse: undefined,
            newPassword: new ValidatedValue("", [notEmptyValidator, passwordValidator]),
            confirmPassword: new ValidatedValue("", [notEmptyValidator])
        };

        const confirmPasswordValidator = {
            name: "confirmPasswordIsCorrect",
            fn: (value: string) => {
                return (value === this.state.newPassword.value);
            },
            errorMessage: () => `${i18n.t("administration.addUser.errors.incorrectConfirmPassword")}`
        };
        this.state.confirmPassword.addValidators(confirmPasswordValidator);
    }

    render() {
        return (
            <Popup
                open={this.props.displayPopup}
                className="change-user-password-popup"
                header={i18n.t("administration.changeUserPasswordAction.header")}
                content={this.renderContent()}
                actionText={i18n.t("administration.changeUserPasswordAction.actionText")}
                action={this.confirmChangePassword.bind(this)}
                cancel={() => {
                    this.closePopup();
                }}
                testId="change-user-password-popup"
                httpError={
                    <>
                        {!Api.isSuccess(this.state.changePasswordResponse) && (
                            <HttpError
                                response={this.state.changePasswordResponse}
                                cause={i18n.t("administration.errors.changePasswordErrorSuffix")}
                                translatedFields={{
                                    id: i18n.t("administration.errors.idNotFound")
                                }}
                            />
                        )}
                    </>
                }
            />
        );
    }

    private renderContent() {
        const {newPassword, confirmPassword} = this.state;
        return (
            <div className="form">
                <ChangePasswordForm
                    isAdminMode
                    username={new ValidatedValue<string>(this.props.username)}
                    newPassword={newPassword}
                    confirmPassword={confirmPassword}
                />
            </div>
        );
    }

    public get isValidChangeForm() {
        this.state.newPassword.validate();
        this.state.confirmPassword.validate();

        return this.state.newPassword.isValid && this.state.confirmPassword.isValid;
    }

    private async confirmChangePassword() {
        this.setState({changePasswordResponse: undefined});

        if (this.props.userId && this.isValidChangeForm) {
            this.setState({changePasswordResponse: await AdministrationService.temporaryUserPassword(
                this.props.userId,
                this.state.newPassword.value,
                this.state.confirmPassword.value
            )});
        }

        if (this.state.changePasswordResponse && Api.isSuccess(this.state.changePasswordResponse)) {
            this.closePopup();
            this.props.confirmSuccessAction();
        }

        Utils.scrollToFirstErrorMessage();
    }

    private closePopup(){
        this.setState({
            changePasswordResponse: undefined
        });
        this.state.newPassword.setValue("");
        this.state.confirmPassword.setValue("");
        this.state.newPassword.clearValidation();
        this.state.confirmPassword.clearValidation();
        this.props.closePopup();
    }
}
